import React, { useEffect, useLayoutEffect } from 'react';
import { useQuery } from '@apollo/client';
import { AdsChunkSize } from './AdsWithInfiniteScroll.types';
import { AdsSection } from '@/components/AdsSection/AdsSection';
import { GetAdsResponse, ServiceName } from '@/api/types/ads.types';
import { GET_ADS } from '@/api/queries/ads.queries';
import { useSaveAdsHandlersStore } from '@/store/useSaveAdsHandlersStore';
import { STORAGE_KEYS } from '@/constants/storageKeys';
import { useParseQuery } from '@/hooks/useParseQuery/useParseQuery';
import { ScrollSaver } from '@/components/ScrollSaver/ScrollSaver';
import { PAGE_WITH_ADS } from '@/constants/common';
import { getSavedAdsData } from '@/components/ScrollSaver/ScrollSaver.helpers';
import { mergeAdsResults } from '@/utils/helpers';
import { useCurrentCategory } from '@/hooks/useCurrentCategory/useCurrentCategory';
import { usePreviousValue } from '@/hooks/usePreviousValue';
import { usePopState } from '@/hooks/usePopState';
import { usePlatformSource } from '@/hooks/usePlatformSource';

export const UserAdsWithInfiniteScroll = () => {
  const { setSaveLastAdsHandler } = useSaveAdsHandlersStore();
  const { userLegacyId, propertyOptions } = useParseQuery();
  const { isAllCategories, currentCategory } = useCurrentCategory();
  const { latestAdsData: savedAdsData } =
    getSavedAdsData(PAGE_WITH_ADS.USER_ADS) || {};
  const { isPopState, resetPopState } = usePopState();
  const source = usePlatformSource();

  const filters = {
    categoryId: isAllCategories ? null : currentCategory?.id,
    userLegacyId,
    propertyOptions,
  };

  const { data, loading, fetchMore, updateQuery, refetch } =
    useQuery<GetAdsResponse>(GET_ADS(ServiceName.Latest), {
      variables: {
        first: AdsChunkSize.Large,
        filters,
        source,
      },
      notifyOnNetworkStatusChange: true,
    });

  const resetKey = JSON.stringify(filters);

  const prevResetKey = usePreviousValue(resetKey);

  useEffect(() => {
    if (
      prevResetKey && // to avoid resetting on the first render
      prevResetKey !== resetKey &&
      !isPopState
    ) {
      refetch();
    }
    resetPopState();
  }, [resetKey]);

  useLayoutEffect(() => {
    if (!data && savedAdsData) {
      updateQuery(() => savedAdsData);
    }
  }, []);

  useEffect(() => {
    setSaveLastAdsHandler(() => {
      const newData = JSON.stringify({
        page: PAGE_WITH_ADS.USER_ADS,
        latestAdsData: data,
      });
      localStorage.setItem(STORAGE_KEYS.LAST_ADS_DATA, newData);
    });
  }, [data]);

  const ads = data?.ads?.nodes || [];
  const { endCursor, hasNextPage } = data?.ads?.pageInfo || {};

  function onLoadMore() {
    if (loading || !hasNextPage || !navigator.onLine) return;

    fetchMore({
      variables: {
        after: endCursor,
      },
      updateQuery: (prevResult, { fetchMoreResult }) =>
        mergeAdsResults(prevResult, fetchMoreResult),
    });
  }

  return (
    <>
      <ScrollSaver page={PAGE_WITH_ADS.USER_ADS} />
      <AdsSection
        ads={ads}
        adsLoading={loading}
        chunkSize={AdsChunkSize.Large}
        onLoadMore={onLoadMore}
      />
    </>
  );
};
